/**
 * useStatementQuery — state for the PQL statement bar (the item list, and the
 * Report Designer).
 *
 * Owns the builder ⇄ statement mode toggle, the statement text, live parsing
 * against the same resolved field defs the filter builder uses, the `pql` URL
 * param on the item list (every query is a shareable link), and lossless mode
 * switching:
 *   - builder → statement: prefills the printed form of the current filters.
 *   - statement → builder: converts the AST back to filter groups when the
 *     query is two-level representable; queries using negation or deeper
 *     nesting stay statement-only (canExitToBuilder === false).
 *
 * Parsing is live (each keystroke): a valid statement applies immediately
 * (including ORDER BY → the caller's sort); an invalid one keeps the last good
 * AST applied and surfaces the error inline.
 *
 * Nothing here belongs to the item list's page: filters and sort come in as
 * arguments, and the URL is only touched when `syncUrl` is on (the default).
 * The Report Designer turns it off — its statement is the saved report's, and
 * the designer's own address carries the report, not a query.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useItemListPqlCatalog } from './useItemListPqlCatalog';
import type { FilterGroup, SortSpec } from './useItemListState';
import {
  astToFilterGroups,
  filterGroupsToAst,
  printQuery,
  safeParse,
  toEnglish,
  type PqlCatalog,
  type PqlError,
  type PqlQuery,
  type QueryNode,
} from '../../../../lib/pql';
import { repairPqlStatement, translateEnglishToPql } from '../../../../lib/pql/ai/nlToPql';
import { isAiCapabilityEnabled } from '../../../../lib/ai/aiAssistSettings';

export type StatementMode = 'builder' | 'statement';
export type AiStatus = 'idle' | 'loading' | 'error';

export interface StatementQueryState {
  mode: StatementMode;
  text: string;
  setText: (text: string) => void;
  /** The AST currently applied to the list (last good parse). Null = match everything. */
  appliedAst: QueryNode | null;
  /** The whole applied query (predicate + ORDER BY) — what Save / Create verbs persist. */
  appliedQuery: PqlQuery;
  /** Canonical printed form of the applied query (parse ∘ print fixed point). */
  canonicalText: string;
  /** The vocabulary the parser validates against — drives autocomplete. */
  catalog: PqlCatalog;
  /** Parse error for the current text, or null when it parses. */
  error: PqlError | null;
  /** Deterministic plain-English rendering of the applied query (PRD §3.2). */
  english: string;
  /** True when the applied query can be shown in the two-level filter builder. */
  canExitToBuilder: boolean;
  enterStatementMode: () => void;
  /** Open statement mode with a given statement (applying a saved query, a shared link). */
  applyStatement: (text: string) => void;
  exitToBuilder: () => boolean;
  clear: () => void;
  /** Whether the workspace has AI query authoring turned on (registry + env flag + entitlement). */
  aiEnabled: boolean;
  aiStatus: AiStatus;
  aiMessage: string | null;
  /** Translate English into a statement — result lands in the editable text box, never applied silently. */
  askAi: (english: string) => Promise<void>;
  /** Ask AI to fix the current (currently-invalid) statement. No-op if the statement already parses. */
  fixWithAi: () => Promise<void>;
}

interface UseStatementQueryParams {
  filterGroups: FilterGroup[];
  replaceFilters: (groups: FilterGroup[]) => void;
  sort: SortSpec[];
  setSort: (specs: SortSpec[]) => void;
  /** Read and write the `pql` URL param. Default true (the item list). */
  syncUrl?: boolean;
  /** Open in statement mode with this text (when the URL carries none). */
  initialStatement?: string;
}

export function useStatementQuery({
  filterGroups,
  replaceFilters,
  sort,
  setSort,
  syncUrl = true,
  initialStatement,
}: UseStatementQueryParams): StatementQueryState {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlStatement = syncUrl ? searchParams.get('pql') : null;

  // Same vocabulary as FilterPanel and the ⌘K command
  // bar — one registry, one catalog.
  const catalog = useItemListPqlCatalog();

  // A `pql` URL param on load means the link carries a statement.
  const [mode, setMode] = useState<StatementMode>(() =>
    urlStatement !== null || initialStatement !== undefined ? 'statement' : 'builder',
  );
  const [text, setTextState] = useState(() => urlStatement ?? initialStatement ?? '');
  /** The last `pql` value this hook itself wrote — anything else came from outside. */
  const lastWrittenParamRef = useRef<string | null>(urlStatement);

  const parseResult = useMemo(() => safeParse(text, catalog), [text, catalog]);

  // Last good AST — an invalid keystroke keeps the previous result on screen.
  const lastGoodRef = useRef<QueryNode | null>(null);
  const lastGoodQueryRef = useRef(parseResult.ok ? parseResult.query : { where: null, orderBy: [] });
  if (parseResult.ok) {
    lastGoodRef.current = parseResult.query.where;
    lastGoodQueryRef.current = parseResult.query;
  }
  const appliedAst = parseResult.ok ? parseResult.query.where : lastGoodRef.current;

  const setPqlParam = useCallback(
    (value: string | null) => {
      lastWrittenParamRef.current = value;
      if (!syncUrl) return;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value !== null) next.set('pql', value);
          else next.delete('pql');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, syncUrl],
  );

  const setText = useCallback(
    (t: string) => {
      lastWrittenParamRef.current = t;
      setTextState(t);
      setPqlParam(t);
    },
    [setPqlParam],
  );

  // Adopt a `pql` param this hook did not write — the ⌘K command bar navigating
  // to /prioritization/items?pql=… while the list is already mounted, or a
  // browser back/forward between two queries. Without this the URL changes and
  // the bar keeps showing the previous statement.
  //
  // No loop: every write goes through setPqlParam, which records the value in
  // lastWrittenParamRef first, so the effect only fires for params from
  // elsewhere.
  const pqlParam = urlStatement;
  useEffect(() => {
    if (pqlParam === null) return;
    if (pqlParam === lastWrittenParamRef.current) return;
    lastWrittenParamRef.current = pqlParam;
    setTextState(pqlParam);
    setMode('statement');
  }, [pqlParam]);

  // ORDER BY in the statement drives the list's sort control.
  useEffect(() => {
    if (mode !== 'statement' || !parseResult.ok) return;
    const orderBy = parseResult.query.orderBy;
    if (orderBy.length === 0) return;
    if (JSON.stringify(orderBy) !== JSON.stringify(sort)) setSort(orderBy);
  }, [mode, parseResult, sort, setSort]);

  const enterStatementMode = useCallback(() => {
    const prefill = printQuery({ where: filterGroupsToAst(filterGroups), orderBy: [] });
    setTextState(prefill);
    setPqlParam(prefill);
    setMode('statement');
  }, [filterGroups, setPqlParam]);

  const applyStatement = useCallback(
    (t: string) => {
      setTextState(t);
      setPqlParam(t);
      setMode('statement');
    },
    [setPqlParam],
  );

  const canExitToBuilder = useMemo(
    () => astToFilterGroups(appliedAst) !== null,
    [appliedAst],
  );

  const exitToBuilder = useCallback((): boolean => {
    const groups = astToFilterGroups(appliedAst);
    if (groups === null) return false;
    replaceFilters(groups);
    setPqlParam(null);
    setMode('builder');
    return true;
  }, [appliedAst, replaceFilters, setPqlParam]);

  const clear = useCallback(() => setText(''), [setText]);

  const english = useMemo(
    () => toEnglish(lastGoodQueryRef.current, catalog),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ref updated in render when parse succeeds
    [parseResult, catalog],
  );

  // In builder mode the applied query IS the builder's filter groups — the
  // downstream verbs (save, create collection, add to pool) read one object
  // whichever mode is showing (PRD §2.1: one AST, three representations).
  const appliedQuery = useMemo<PqlQuery>(() => {
    if (mode === 'statement') return lastGoodQueryRef.current;
    return { where: filterGroupsToAst(filterGroups), orderBy: [] };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ref updated in render when parse succeeds
  }, [mode, parseResult, filterGroups]);
  const canonicalText = useMemo(() => printQuery(appliedQuery), [appliedQuery]);

  // ── AI-assisted authoring — NL → PQL, and "fix with AI" on parse errors ────
  // The AI layer only ever proposes text into `text`; it never bypasses the
  // parser above. See src/lib/pql/ai/nlToPql.ts for the glass-box discipline.
  const aiEnabled = useMemo(() => isAiCapabilityEnabled('nl_to_pql'), []);
  const [aiStatus, setAiStatus] = useState<AiStatus>('idle');
  const [aiMessage, setAiMessage] = useState<string | null>(null);

  const askAi = useCallback(
    async (english: string) => {
      setAiStatus('loading');
      setAiMessage(null);
      setMode('statement');
      const result = await translateEnglishToPql(english, catalog);
      if (result.ok && result.statement) {
        setText(result.statement);
        setAiStatus('idle');
        setAiMessage(result.explanation ?? null);
      } else {
        setAiStatus('error');
        setAiMessage(result.reason ?? 'AI could not translate that.');
      }
    },
    [catalog, setText],
  );

  const fixWithAi = useCallback(async () => {
    if (parseResult.ok) return;
    setAiStatus('loading');
    setAiMessage(null);
    const result = await repairPqlStatement(text, parseResult.error.message, catalog);
    if (result.ok && result.statement) {
      setText(result.statement);
      setAiStatus('idle');
      setAiMessage(result.explanation ?? null);
    } else {
      setAiStatus('error');
      setAiMessage(result.reason ?? 'AI could not fix that statement.');
    }
  }, [text, parseResult, catalog, setText]);

  return {
    mode,
    text,
    setText,
    appliedAst,
    appliedQuery,
    canonicalText,
    catalog,
    error: parseResult.ok ? null : parseResult.error,
    english,
    canExitToBuilder,
    enterStatementMode,
    applyStatement,
    exitToBuilder,
    clear,
    aiEnabled,
    aiStatus,
    aiMessage,
    askAi,
    fixWithAi,
  };
}
